import React, { useState } from 'react';
import { Maximize2 } from 'react-feather';
import ChatMarkdown from './ChatMarkdown';
import FocusView from './FocusView';

const StudyNotesView = ({ content, documentName, document }) => {
  const [showFocusView, setShowFocusView] = useState(false);

  // Enhanced styles for study notes
  const notesStyles = `
    /* Main content styling */
    .study-notes-content {
      scroll-behavior: smooth;
    }
    
    /* Document title */
    .study-notes-content h1:first-of-type {
      font-size: 32px;
      font-weight: 700;
      margin: 0 0 16px 0;
      padding-bottom: 16px;
      border-bottom: 2px solid rgba(102, 126, 234, 0.3);
      color: #fff;
    }
    
    /* Part/Section titles (h1) */
    .study-notes-content h1 {
      font-size: 26px;
      font-weight: 700;
      margin: 48px 0 24px 0;
      padding: 16px 20px;
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.15) 0%, rgba(118, 75, 162, 0.1) 100%);
      border-left: 4px solid #667eea;
      border-radius: 0 12px 12px 0;
      color: #fff;
    }
    
    /* Table of Contents heading */
    .study-notes-content h2:first-of-type,
    .study-notes-content h2[id*="table"],
    .study-notes-content h2:has(+ ul a[href^="#"]) {
      font-size: 20px;
      font-weight: 600;
      margin: 24px 0 16px 0;
      color: rgba(255, 255, 255, 0.9);
    }
    
    /* Section headings (h2) */
    .study-notes-content h2 {
      font-size: 22px;
      font-weight: 600;
      margin: 36px 0 18px 0;
      padding-bottom: 10px;
      border-bottom: 1px solid rgba(102, 126, 234, 0.2);
      color: #a5b4fc;
    }
    
    /* Subsection headings (h3) */
    .study-notes-content h3 {
      font-size: 18px;
      font-weight: 600;
      margin: 28px 0 14px 0;
      color: rgba(255, 255, 255, 0.95);
      display: flex;
      align-items: center;
      gap: 10px;
    }
    
    .study-notes-content h3::before {
      content: '';
      width: 4px;
      height: 18px;
      background: linear-gradient(180deg, #667eea, #764ba2);
      border-radius: 2px;
    }
    
    /* h4 for sub-subsections */
    .study-notes-content h4 {
      font-size: 16px;
      font-weight: 600;
      margin: 20px 0 12px 0;
      color: rgba(255, 255, 255, 0.85);
    }
    
    /* Paragraphs */
    .study-notes-content p {
      margin: 14px 0;
      line-height: 1.8;
      color: rgba(255, 255, 255, 0.85);
    }
    
    /* Lists */
    .study-notes-content ul, .study-notes-content ol {
      margin: 16px 0;
      padding-left: 24px;
    }
    
    .study-notes-content li {
      margin: 10px 0;
      line-height: 1.7;
      color: rgba(255, 255, 255, 0.85);
    }
    
    .study-notes-content li::marker {
      color: #667eea;
    }
    
    /* TOC Links */
    .study-notes-content ul a[href^="#"] {
      color: #818cf8;
      text-decoration: none;
      padding: 6px 12px;
      display: inline-block;
      border-radius: 6px;
      transition: all 0.2s ease;
      border: 1px solid transparent;
    }
    
    .study-notes-content ul a[href^="#"]:hover {
      background: rgba(102, 126, 234, 0.15);
      border-color: rgba(102, 126, 234, 0.3);
      color: #a5b4fc;
    }
    
    /* Tables */
    .study-notes-content table {
      width: 100%;
      margin: 20px 0;
      border-collapse: collapse;
      border-radius: 10px;
      overflow: hidden;
      background: rgba(0, 0, 0, 0.2);
    }
    
    .study-notes-content thead {
      background: linear-gradient(135deg, rgba(102, 126, 234, 0.2) 0%, rgba(118, 75, 162, 0.15) 100%);
    }
    
    .study-notes-content th {
      padding: 14px 16px;
      text-align: left;
      font-weight: 600;
      color: #a5b4fc;
      border-bottom: 2px solid rgba(102, 126, 234, 0.3);
      font-size: 14px;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
    
    .study-notes-content td {
      padding: 12px 16px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.06);
      color: rgba(255, 255, 255, 0.85);
      font-size: 15px;
    }
    
    .study-notes-content tr:last-child td {
      border-bottom: none;
    }
    
    .study-notes-content tr:hover td {
      background: rgba(102, 126, 234, 0.05);
    }
    
    /* Blockquotes - for document excerpts */
    .study-notes-content blockquote {
      margin: 20px 0;
      padding: 16px 20px;
      background: rgba(102, 126, 234, 0.08);
      border-left: 4px solid #667eea;
      border-radius: 0 10px 10px 0;
      font-style: italic;
      color: rgba(255, 255, 255, 0.8);
    }
    
    /* Code/formulas */
    .study-notes-content code {
      background: rgba(102, 126, 234, 0.12);
      padding: 3px 8px;
      border-radius: 5px;
      font-size: 14px;
      font-family: 'JetBrains Mono', 'Fira Code', monospace;
      color: #c7d2fe;
    }
    
    .study-notes-content pre {
      margin: 20px 0;
      padding: 20px;
      background: rgba(0, 0, 0, 0.35);
      border-radius: 10px;
      border: 1px solid rgba(102, 126, 234, 0.15);
      overflow-x: auto;
    }
    
    /* Strong/emphasis */
    .study-notes-content strong {
      font-weight: 600;
      color: #fff;
    }
    
    .study-notes-content em {
      font-style: italic;
      color: rgba(255, 255, 255, 0.9);
    }
    
    /* Horizontal rules - section dividers */
    .study-notes-content hr {
      margin: 40px 0;
      border: none;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(102, 126, 234, 0.3), transparent);
    }
    
    /* Anchor targets - smooth scroll offset */
    .study-notes-content [id] {
      scroll-margin-top: 20px;
    }
    
    /* Math/LaTeX styling */
    .study-notes-content .katex-display {
      margin: 24px 0;
      padding: 20px;
      background: rgba(0, 0, 0, 0.2);
      border-radius: 10px;
      border: 1px solid rgba(102, 126, 234, 0.1);
      overflow-x: auto;
    }
  `;

  return (
    <>
      <div style={{ 
        height: '100%', 
        overflowY: 'auto',
        background: 'linear-gradient(to bottom, var(--bg-primary) 0%, #0d0d10 100%)'
      }}>
        {/* Focus Mode Button */}
        {document && (
          <div style={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '12px 24px',
            background: 'linear-gradient(to bottom, var(--bg-primary) 0%, transparent 100%)',
          }}>
            <button
              onClick={() => setShowFocusView(true)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 16px',
                background: 'rgba(102, 126, 234, 0.1)',
                border: '1px solid rgba(102, 126, 234, 0.3)',
                borderRadius: '8px',
                color: '#667eea',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(102, 126, 234, 0.2)';
                e.target.style.borderColor = '#667eea';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(102, 126, 234, 0.1)';
                e.target.style.borderColor = 'rgba(102, 126, 234, 0.3)';
              }}
            >
              <Maximize2 size={14} />
              Focus Mode
            </button>
          </div>
        )}

        <div style={{
          maxWidth: '900px',
          margin: '0 auto',
          padding: document ? '12px 40px 48px' : '48px 40px',
        }}>
          <div style={{
            background: 'linear-gradient(135deg, rgba(15, 15, 20, 0.95) 0%, rgba(20, 20, 30, 0.9) 100%)',
            border: '1px solid rgba(102, 126, 234, 0.12)',
            borderRadius: '20px',
            padding: '48px',
            boxShadow: '0 12px 48px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.02)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {/* Decorative gradient overlay */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              background: 'linear-gradient(90deg, #667eea 0%, #764ba2 50%, #a855f7 100%)',
            }} />
            
            {/* Content */}
            <div 
              className="study-notes-content"
              style={{
                fontSize: '16px',
                lineHeight: '1.75',
                color: 'rgba(255, 255, 255, 0.85)',
              }}
            >
              <style>{notesStyles}</style>
              <ChatMarkdown content={content} />
            </div>
          </div>
        </div>
      </div>

      {/* Focus View Modal */}
      {showFocusView && document && (
        <FocusView
          document={document}
          content={content}
          contentType="notes"
          title={`Notes: ${documentName || document.name}`}
          onClose={() => setShowFocusView(false)}
        />
      )}
    </>
  );
};

export default StudyNotesView;
